import { MyShoppingListDraft } from "@commercetools/platform-sdk";
import { PrintProvider, ProductConfiguration } from "@wttb/product-configurator";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import Button from "@/common/components/button";
import { Input } from "@/common/components/form/input";
import { Modal } from "@/common/components/modal";
import SaveIcon from "@/common/icons/save-icon";
import { useCreateShoppingListMutation } from "@/features/account/hooks/use-create-shopping-list";
import { useShoppingListQuery } from "@/features/account/hooks/use-shopping-list";
import { productTestIds } from "@/features/product/test-ids";

type FormValues = {
  name: string;
};

const defaultValues: FormValues = {
  name: "",
};

interface ShoppingListModalFormTypes {
  isOpen: boolean;
  sku: string;
  config: ProductConfiguration;
  printProvider: PrintProvider;
  handleOnClose: () => void;
}

export function ShoppingListModalForm({
  isOpen,
  sku,
  config,
  printProvider,
  handleOnClose,
}: ShoppingListModalFormTypes) {
  const {
    register,
    handleSubmit,
    resetField,
    formState: { errors },
    setError,
  } = useForm<FormValues>({
    defaultValues,
  });
  const t = useTranslations("common");
  const { mutateAsync: createShoppingList, isLoading } = useCreateShoppingListMutation();
  const { data } = useShoppingListQuery();
  const shoppingList = data?.results || [];

  const onSubmit = async ({ name }: { name: string }) => {
    const isShoppingListNameExist = shoppingList.some(
      (item: MyShoppingListDraft) => item.name.en === name,
    );

    if (isShoppingListNameExist) {
      setError("name", {
        type: "manual",
        message: `${name} already exists.`,
      });
    } else {
      await createShoppingList({ name, sku, config, printProvider });
      toast("Quote saved successfully.");
      resetField("name");
      handleOnClose();
    }
  };
  return (
    <Modal open={isOpen} onClose={handleOnClose}>
      <div className="lg:flex-column mx-auto flex w-full flex-col overflow-hidden md:w-[500px] lg:w-[500px]">
        <div className="max-h-430px flex w-full shrink-0 items-center justify-center overflow-hidden lg:max-h-full">
          <div className="p-8">
            <div className="mb-4 flex justify-center">
              <SaveIcon />
            </div>
            <h2 className="text-center text-2xl font-semibold text-inherit">
              {t("save-quote-modal-heading")}
            </h2>
            <p className="my-4 text-center text-base leading-6">
              {t.rich("save-quote-modal-description", {
                highlight: (myQuote: React.ReactNode) => (
                  <span className="font-semibold">{myQuote}</span>
                ),
              })}
            </p>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="mt-8 flex flex-col justify-center"
              noValidate
            >
              <Input
                label={`${t("save-quote-modal-input-label")}:`}
                type="text"
                variant="solid"
                {...register("name", {
                  required: t("save-quote-modal-input-error"),
                })}
                error={errors.name && errors.name.message!}
                labelClassName="!font-normal"
              />
              <Button
                disabled={isLoading}
                data-testid={productTestIds.saveToShoppingListModalSaveBtn}
                variant="primary"
                className={`mx-auto mt-4 h-12 w-[240px] px-1.5 uppercase`}
              >
                {t("text-save-to-shopping-list")}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </Modal>
  );
}
